/**
 * A figure, and the one number a screen is about — SPEC §6.
 *
 * The fourth of the four abstractions, and the smallest: a value, its label, and the server's own
 * sentence under it. It exists because **every number on this console is
 * `font-variant-numeric: tabular-nums`**, and a column of prices that shifts as it ticks is
 * unreadable. One component is how that stays true without every panel remembering it.
 *
 * `moved` paints 漲紅跌綠 and nothing else: `--up` for a rise, `--down` for a fall, `--flat`
 * otherwise. Never `--accept` or `--danger`, which are a confirmation and a kill switch.
 */

import type { ReactNode } from "react";
import { moved } from "../../lib/fmt";
import { Info } from "./Info";

interface Props {
  label: string;
  /** The ⓘ definition beside the label. */
  info?: string;
  value: ReactNode;
  /** The server's sentence about this figure, when it wrote one. */
  says?: ReactNode;
  /** The signed number this figure is about, if it is one, so it can be painted. */
  direction?: number | null;
}

export function Figure({ label, info, value, says, direction }: Props) {
  const paint = direction === undefined ? null : moved(direction);
  return (
    <div className="fig">
      <span className="lbl">
        {label}
        {info ? <Info term={label} text={info} /> : null}
      </span>
      <span className={`fig__v${paint ? ` ${paint}` : ""}`}>{value}</span>
      {says ? <span className="fig__says">{says}</span> : null}
    </div>
  );
}

/** A row of figures, which is how C3, C4, C6 and C8 all read. */
export function Figures({ children }: { children: ReactNode }) {
  return <div className="figs">{children}</div>;
}

/** A label → value line, for the panels that are a list of amounts rather than a table. */
export function Line({
  label,
  value,
  net,
  direction,
}: {
  label: string;
  value: ReactNode;
  net?: boolean;
  direction?: number | null;
}) {
  const paint = direction === undefined ? null : moved(direction);
  return (
    <div className={`money__row${net ? " money__row--net" : ""}`}>
      <dt>{label}</dt>
      <dd className={paint ?? undefined}>{value}</dd>
    </div>
  );
}
